/**
 * Session Participants Store - session-level direct user participation (session_participants).
 * Canonical source when present; session.participant_ids used as fallback for backward compat.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type { ParticipantRole } from './cycleParticipantsStore';

export type SessionParticipantStatus = 'active' | 'removed';

export interface SessionParticipant {
  id: string;
  sessionId: string;
  userId: string;
  role: ParticipantRole;
  status: SessionParticipantStatus;
  addedBy: string;
  addedAt: Date;
}

interface SessionParticipantsState {
  participants: SessionParticipant[];
  isLoading: boolean;
  error: string | null;
  fetchParticipantsForSession: (sessionId: string) => Promise<void>;
  addParticipant: (input: { sessionId: string; userId: string; role: ParticipantRole; addedBy: string }) => Promise<SessionParticipant | null>;
  /**
   * Change an existing participant's role in place. `addParticipant` returns an
   * existing row untouched, so callers used to remove-then-add to change a role
   * — two fire-and-forget writes racing each other, which could delete the
   * participant after re-adding them (F7: "the chosen decider persists").
   */
  setParticipantRole: (input: { sessionId: string; userId: string; role: ParticipantRole }) => Promise<boolean>;
  removeParticipant: (id: string) => Promise<boolean>;
  getParticipantsForSession: (sessionId: string) => SessionParticipant[];
  getActiveParticipantIdsForSession: (sessionId: string) => string[];
}

const TABLE_NAME = 'session_participants';

/**
 * Keep `prioritization_sessions.participant_ids` in step with the rows.
 *
 * This table is canonical, but the denormalized column is still what several
 * surfaces count — the live room header and the facilitator's conductor panel
 * among them. Writing a row without the column left a session with five
 * participants reporting a room of one, so the facilitator saw "0 / 1
 * submitted" while four people were waiting to be counted.
 */
async function syncParticipantIdsColumn(sessionId: string): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('user_id')
      .eq('session_id', sessionId)
      .eq('status', 'active');
    if (error || !data) return;
    const ids = [...new Set((data as { user_id: string }[]).map((r) => r.user_id).filter(Boolean))];
    await supabase.from('prioritization_sessions').update({ participant_ids: ids }).eq('id', sessionId);

    // The in-memory session carries the same stale array until something
    // refetches, and the readouts render from memory the moment the session is
    // created — which is exactly when a five-person room read "1 participant".
    try {
      const { useSessionStore } = await import('./sessionStore');
      useSessionStore.setState((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === sessionId ? { ...s, participantIds: ids } : s,
        ),
      }));
    } catch {
      // The row write above is the durable half; a stale in-memory copy
      // corrects itself on the next fetch.
    }
  } catch {
    // The rows are canonical; a stale column is a display defect, not a data loss.
  }
}

function rowToParticipant(row: Record<string, unknown>): SessionParticipant {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    userId: row.user_id as string,
    role: row.role as ParticipantRole,
    status: (row.status as SessionParticipantStatus) || 'active',
    addedBy: row.added_by as string,
    addedAt: row.added_at != null ? new Date(row.added_at as string) : new Date(0),
  };
}

function participantToRow(p: Partial<SessionParticipant>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.id != null) out.id = p.id;
  if (p.sessionId != null) out.session_id = p.sessionId;
  if (p.userId != null) out.user_id = p.userId;
  if (p.role != null) out.role = p.role;
  if (p.status != null) out.status = p.status;
  if (p.addedBy != null) out.added_by = p.addedBy;
  if (p.addedAt != null) out.added_at = p.addedAt.toISOString();
  return out;
}

export const useSessionParticipantsStore = create<SessionParticipantsState>()((set, get) => ({
  participants: [],
  isLoading: false,
  error: null,

  fetchParticipantsForSession: async (sessionId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('session_id', sessionId)
        .order('added_at', { ascending: true });
      if (error) {
        if (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist')) {
          set(state => {
            const other = state.participants.filter(p => p.sessionId !== sessionId);
            return { participants: other, isLoading: false, error: null };
          });
          return;
        }
        set({ isLoading: false, error: error.message });
        return;
      }
      const list = (data || []).map(rowToParticipant);
      set(state => {
        const other = state.participants.filter(p => p.sessionId !== sessionId);
        return { participants: [...other, ...list], isLoading: false, error: null };
      });
    } catch (err) {
      set({ isLoading: false, error: String(err) });
    }
  },

  addParticipant: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    try {
      const { data: existing } = await supabase
        .from(TABLE_NAME)
        .select('id, status')
        .eq('session_id', input.sessionId)
        .eq('user_id', input.userId)
        .maybeSingle();
      if (existing) {
        if (existing.status === 'removed') {
          const { data: updated, error: upErr } = await supabase
            .from(TABLE_NAME)
            .update({ status: 'active' })
            .eq('id', existing.id)
            .select()
            .single();
          if (upErr) {
            set({ error: upErr.message });
            return null;
          }
          const p = rowToParticipant(updated);
          set(state => ({
            participants: state.participants.map(x => (x.id === p.id ? p : x)),
            error: null,
          }));
          return p;
        }
        return rowToParticipant(existing as unknown as Record<string, unknown>);
      }
      const newRow: SessionParticipant = {
        id: generateUUID(),
        sessionId: input.sessionId,
        userId: input.userId,
        role: input.role,
        status: 'active',
        addedBy: input.addedBy,
        addedAt: new Date(),
      };
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(participantToRow(newRow))
        .select()
        .single();
      if (error) {
        set({ error: error.message });
        return null;
      }
      const inserted = rowToParticipant(data as unknown as Record<string, unknown>);
      set(state => ({ participants: [...state.participants, inserted], error: null }));
      void syncParticipantIdsColumn(input.sessionId);
      return inserted;
    } catch (err) {
      set({ error: String(err) });
      return null;
    }
  },

  setParticipantRole: async ({ sessionId, userId, role }) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({ role })
        .eq('session_id', sessionId)
        .eq('user_id', userId)
        .select()
        .maybeSingle();
      if (error) {
        set({ error: error.message });
        return false;
      }
      if (!data) return false;
      const p = rowToParticipant(data as unknown as Record<string, unknown>);
      set((state) => ({
        participants: state.participants.map((x) => (x.id === p.id ? p : x)),
        error: null,
      }));
      return true;
    } catch (e) {
      set({ error: String(e) });
      return false;
    }
  },

  removeParticipant: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const p = get().participants.find(x => x.id === id);
    if (!p) return false;
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ status: 'removed' })
        .eq('id', id);
      if (error) {
        set({ error: error.message });
        return false;
      }
      set(state => ({
        participants: state.participants.map(x =>
          x.id === id ? { ...x, status: 'removed' as SessionParticipantStatus } : x
        ),
        error: null,
      }));
      return true;
    } catch (err) {
      set({ error: String(err) });
      return false;
    }
  },

  getParticipantsForSession: (sessionId) =>
    get().participants.filter(p => p.sessionId === sessionId),
  getActiveParticipantIdsForSession: (sessionId) =>
    get()
      .participants.filter(p => p.sessionId === sessionId && p.status === 'active')
      .map(p => p.userId),
}));
